import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Skeleton,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { Refresh } from "@mui/icons-material";
import { statusColor, statusLabel } from "./toolsData";
import "./tools.css";

const navigation = [
  ["Overview", "/home/tools-market"],
  ["Products & inventory", "/home/tools"],
  ["Orders", "/home/tools-orders"],
  ["Receipt review", "/home/tools-payments"],
  ["Banners", "/home/banners"],
  ["Categories", "/home/categories"],
  ["Settings", "/home/tools-settings"],
];
export default function ToolsLayout({ title, description, actions, children }) {
  const { pathname } = useLocation();
  const active = /tools-order\//.test(pathname)
    ? "/home/tools-orders"
    : /add-tool|edit-tool|view-tool/.test(pathname)
      ? "/home/tools"
      : /add-banner|edit-banner/.test(pathname)
        ? "/home/banners"
        : /add-form/.test(pathname)
          ? "/home/categories"
          : pathname;
  return (
    <main className="tools-workspace">
      <header className="tools-heading">
        <div>
          <span className="tools-eyebrow">SILKCART / TOOLS MARKET</span>
          <Typography component="h1" variant="h4">
            {title}
          </Typography>
          <p>{description}</p>
        </div>
        <div className="tools-actions">{actions}</div>
      </header>
      <Tabs
        className="tools-nav"
        value={navigation.some(([, path]) => path === active) ? active : false}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        aria-label="Tools Market navigation"
      >
        {navigation.map(([label, path]) => (
          <Tab key={path} label={label} value={path} component={Link} to={path} />
        ))}
      </Tabs>
      {children}
    </main>
  );
}
export const useToolsMobile = () => useMediaQuery("(max-width: 600px)");
export function ToolsState({
  loading,
  error,
  retry,
  empty,
  children,
  emptyTitle = "No records yet",
  emptyMessage = "New records will appear here when they are available.",
  action,
}) {
  if (loading)
    return (
      <Box sx={{ p: 3 }} aria-label="Loading market data">
        {[1, 2, 3].map((key) => (
          <Skeleton key={key} height={64} />
        ))}
      </Box>
    );
  if (error)
    return (
      <Alert
        severity="error"
        sx={{ m: 2 }}
        action={
          <Button color="inherit" onClick={retry}>
            Retry
          </Button>
        }
      >
        Unable to load this data. Please try again.
      </Alert>
    );
  if (empty)
    return (
      <div className="tools-empty">
        <Typography component="h2" variant="h6">
          {emptyTitle}
        </Typography>
        <p>{emptyMessage}</p>
        {action}
      </div>
    );
  return children;
}
export const RefreshTools = ({ onClick, loading }) => (
  <Button
    size="small"
    variant="outlined"
    startIcon={<Refresh />}
    onClick={onClick}
    disabled={loading}
  >
    Refresh
  </Button>
);
export const MarketStatus = ({ status, payment = false }) => (
  <Chip
    size="small"
    variant="outlined"
    label={payment && status === "pending" ? "Awaiting payment" : statusLabel(status)}
    color={statusColor(status)}
  />
);

export function ListFooter({ page, count, pageSize, setPage }) {
  const pages = Math.max(1, Math.ceil(count / pageSize));
  return (
    <div className="tools-footer">
      <span>
        {count
          ? `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, count)} of ${count}`
          : "0 results"}
      </span>
      <div className="tools-actions">
        <Button size="small" disabled={page <= 1} onClick={() => setPage(page - 1)}>
          Previous
        </Button>
        <span>
          Page {page} of {pages}
        </span>
        <Button size="small" disabled={page >= pages} onClick={() => setPage(page + 1)}>
          Next
        </Button>
      </div>
    </div>
  );
}
